import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Currency } from 'app/models/currency.model';
import {
  ComboProductStructure, DisplayedComboGroupStructure, DisplayedComboProductStructure,
  DisplayedSize,
  MenuSection,
  OverridePositionRequest, OverridePositionsMap,
  Product
} from 'app/models/menu.model';
import { MatDialog } from '@angular/material/dialog';
import {
  EditProductModalComponent,
  EditProductsModalComponentDataType
} from 'app/pages/menu/shared/edit-product-modal/edit-product-modal.component';
import { Organization } from 'app/models/organization.model';
import { MenuService } from 'app/services/menu.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  buildOverridePositionRequest, hasChangeComboStructure
} from 'app/pages/menu/shared/edit-product-modal/edit-combo-product/edit-combo-product-helper';
import { ProductApiService } from 'app/services/product-api.service';
import { getComboElementKey } from 'app/services/menu-override.service';

// @UntilDestroy()
@Component({
  selector: 'app-edit-combo-product',
  templateUrl: './edit-combo-product.component.html',
  styleUrls: ['./edit-combo-product.component.scss']
})
export class EditComboProductComponent implements OnInit, OnChanges {
  isEditingMode = false;
  isSavingMode = false;
  editComboIconTooltip: string;
  editComboProductIconTooltip: string;

  canEdit: boolean;

  @Input() allowOverrides: boolean;
  @Input() originalComboProductStructure: ComboProductStructure | null;
  @Input() initialComboProduct: DisplayedComboProductStructure | null;
  @Input() comboProductStructure: DisplayedComboProductStructure | null;
  @Input() currency: Currency | null;
  @Input() dictionaryProducts: Map<string, Product>;
  @Input() dictionarySizes: Map<string, DisplayedSize>;
  @Input() originalDictionaryProducts: Map<string, Product>;
  @Input() overridePositionsMap: OverridePositionsMap;
  @Input() currentOrganization: Organization;
  @Input() menuSections: MenuSection[];
  @Input() menuSectionId: string;
  @Input() productId: string;
  @Input() saveForMenuSections: string[];
  @Input() isEditingGroupMode: boolean;
  @Input() externalComboGroupId: string;
  @Input() saveForAllCombo: boolean;
  @Input() saveForAllProductsInComboGroup: boolean;
  @Input() comboGroupStructure: DisplayedComboGroupStructure;
  @Output() changeInformationMessageEvent = new EventEmitter<string[]>();

  changes = [];

  constructor(
    private translate: TranslateService,
    public dialog: MatDialog,
    private menuService: MenuService,
    private productApiService: ProductApiService
  ) {
  }

  ngOnInit(): void {
    this.setCanEdit();
    this.editComboIconTooltip = this.translate.instant('MENU.EDIT_COMBO');
    this.editComboProductIconTooltip = this.translate.instant('MENU.EDIT_PRODUCT');
  }

  ngOnChanges() {
    this.setCanEdit();
  }

  getProductName(comboGroupId: string, externalProductId: string) {
    const comboElementKey = getComboElementKey(comboGroupId, externalProductId);
    return this.dictionaryProducts.get(comboElementKey).name;
  }

  getSizeName(sizeId: string) {
    return this.dictionarySizes.get(sizeId).name;
  }

  async editMode() {
    if(!this.allowOverrides){
      return;
    }
    this.isEditingMode = !this.isEditingMode;
    this.setCanEdit();
    this.editComboIconTooltip = this.translate.instant(this.isEditingMode ? 'COMMON.SAVE' : 'MENU.EDIT_COMBO');
    if (!this.isEditingMode) {
      await this.saveComboProductStructure();
    }
  }

  async saveComboProductStructure() {
    if (!hasChangeComboStructure(this.comboProductStructure, this.initialComboProduct)){
      return;
    }

    let overridePositionsRequest: OverridePositionRequest[] = [];

    this.isSavingMode = true;
    if(this.saveForAllCombo){
      const response = await this.productApiService.getAllComboProductStructures({
        menuId: this.menuSections[0].menuId,
        productStructures: [
          {
            productId: this.comboProductStructure.productId,
            sizeId: this.comboProductStructure.sizeId,
          }
        ]
      });

      const responseItems = response.data ?? [];

      for (let responseItem of responseItems) {
        const overridePositionRequest = buildOverridePositionRequest(
          this.menuSectionId,
          responseItem.parentComboProductId,
          responseItem.externalItemProductId,
          responseItem.sizeId,
          this.overridePositionsMap,
          this.comboProductStructure,
          this.initialComboProduct,
          this.originalComboProductStructure
        );
        overridePositionsRequest.push(overridePositionRequest);
      }
    }
    else{
      const overridePositionRequest = buildOverridePositionRequest(
        this.menuSectionId,
        this.productId,
        getComboElementKey(this.externalComboGroupId, this.comboProductStructure.productId),
        this.comboProductStructure.sizeId,
        this.overridePositionsMap,
        this.comboProductStructure,
        this.initialComboProduct,
        this.originalComboProductStructure
      );
      overridePositionsRequest.push(overridePositionRequest);
    }

    await this.menuService.saveOverridePositions(this.menuSectionId, overridePositionsRequest, this.saveForMenuSections);
    this.changes = [];
    this.changeInformationMessageEvent.emit(this.changes);
    this.isSavingMode = false;
  }

  editProduct() {
    const comboElementId = getComboElementKey(this.externalComboGroupId, this.comboProductStructure.externalProductId);
    const product = this.dictionaryProducts.get(comboElementId);
    const originalProduct = this.originalDictionaryProducts.get(this.comboProductStructure.externalProductId);
    this.dialog.open(EditProductModalComponent, {
      data: {
        product,
        originalProduct,
        menuSections: this.menuSections,
        menuSectionId: this.menuSectionId,
        organization: this.currentOrganization,
        organizationId: this.menuSectionId,
        saveForAllProductsInComboGroup: this.saveForAllProductsInComboGroup,
        comboGroupStructure: this.comboGroupStructure,
        comboElementOverrideKey: comboElementId,
        allowOverrides: this.allowOverrides,
      } as EditProductsModalComponentDataType
    });
  }

  isProductPriceDifferent() {
    return this.comboProductStructure.price != this.originalComboProductStructure.price;
  }

  onChangeInformationMessage() {
    this.changes = [];
    if (this.initialComboProduct.price != this.comboProductStructure.price) {
      this.changes.push(`"Combo Product ${this.getProductName(this.externalComboGroupId, this.comboProductStructure.externalProductId)} Price"`)
    }
    if (this.initialComboProduct.isHidden != this.comboProductStructure.isHidden) {
      this.changes.push(`"Combo Product ${this.getProductName(this.externalComboGroupId, this.comboProductStructure.externalProductId)} IsHidden"`)
    }

    this.changeInformationMessageEvent.emit(this.changes);
  }

  changeIsHidden(event: MatCheckboxChange){
    this.comboProductStructure.isHidden = !event.checked;
  }

  changePrice(event){
    this.comboProductStructure.price = + event.target.value;
  }

  setCanEdit(){
    this.canEdit = this.isEditingGroupMode || this.isEditingMode;
  }
}
